/**
 * useGenerationSessions Hook
 * Manages generation session CRUD, rating, and insights
 */

import { useState, useCallback } from 'react';
import { GenerationSession, DimensionalRating, EffectivenessInsight, SessionFilter } from '@/types/session.types';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

interface SessionListResponse {
  sessions: GenerationSession[];
  total: number;
}

interface RatingAvg {
  overallRating: number | null;
  vocalRating: number | null;
  productionRating: number | null;
  lyricsRating: number | null;
  genreFitRating: number | null;
}

interface GenreAggregate {
  genre: string;
  _avg: RatingAvg;
  _count: { id: number };
}

interface VocalPackAggregate {
  vocalPackKey: string;
  _avg: Pick<RatingAvg, 'overallRating' | 'vocalRating'>;
  _count: { id: number };
}

interface InsightsResponse {
  insights: EffectivenessInsight[];
  topByGenre: GenreAggregate[];
  topByVocalPack: VocalPackAggregate[];
}

interface UseGenerationSessionsReturn {
  sessions: GenerationSession[];
  total: number;
  insights: InsightsResponse | null;
  isLoading: boolean;
  error: string | null;
  createSession: (data: Partial<GenerationSession>) => Promise<GenerationSession | null>;
  fetchSessions: (filter?: SessionFilter) => Promise<void>;
  rateSession: (id: string, ratings: Partial<DimensionalRating> & { notes?: string }) => Promise<void>;
  deleteSession: (id: string) => Promise<void>;
  fetchInsights: () => Promise<void>;
}

export function useGenerationSessions(): UseGenerationSessionsReturn {
  const [sessions, setSessions] = useState<GenerationSession[]>([]);
  const [total, setTotal] = useState(0);
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSession = useCallback(async (data: Partial<GenerationSession>): Promise<GenerationSession | null> => {
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.sessions.create, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed (${res.status})`);
      }

      const session = await res.json();
      console.log('📊 Session created:', session.id);
      return session;
    } catch (err: unknown) {
      console.error('📊 Failed to create session:', err);
      return null;
    }
  }, []);

  const fetchSessions = useCallback(async (filter?: SessionFilter) => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filter?.genre) params.set('genre', filter.genre);
      if (filter?.vocalPackKey) params.set('vocalPackKey', filter.vocalPackKey);
      if (filter?.minRating) params.set('minRating', String(filter.minRating));
      if (filter?.sortBy) params.set('sortBy', filter.sortBy);
      if (filter?.sortOrder) params.set('sortOrder', filter.sortOrder);
      if (filter?.limit) params.set('limit', String(filter.limit));
      if (filter?.offset) params.set('offset', String(filter.offset));

      const res = await fetchWithCsrf(`${API_ENDPOINTS.sessions.list}?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch sessions');

      const data: SessionListResponse = await res.json();
      setSessions(data.sessions);
      setTotal(data.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const rateSession = useCallback(async (id: string, ratings: Partial<DimensionalRating> & { notes?: string }) => {
    try {
      const body: Record<string, number | string> = {};
      if (ratings.overall !== undefined) body.overallRating = ratings.overall;
      if (ratings.vocal !== undefined) body.vocalRating = ratings.vocal;
      if (ratings.production !== undefined) body.productionRating = ratings.production;
      if (ratings.lyrics !== undefined) body.lyricsRating = ratings.lyrics;
      if (ratings.genreFit !== undefined) body.genreFitRating = ratings.genreFit;
      if (ratings.notes !== undefined) body.notes = ratings.notes;

      const res = await fetchWithCsrf(API_ENDPOINTS.sessions.rate(id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Failed to update rating');

      const updated = await res.json();
      setSessions(prev => prev.map(s => s.id === id ? updated : s));
    } catch (err: unknown) {
      console.error('📊 Rating error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  const deleteSession = useCallback(async (id: string) => {
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.sessions.delete(id), { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete session');
      setSessions(prev => prev.filter(s => s.id !== id));
      setTotal(prev => prev - 1);
    } catch (err: unknown) {
      console.error('📊 Delete error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  const fetchInsights = useCallback(async () => {
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.sessions.insights);
      if (!res.ok) throw new Error('Failed to fetch insights');
      const data = await res.json();
      setInsights(data);
    } catch (err: unknown) {
      console.error('📊 Insights error:', err);
    }
  }, []);

  return {
    sessions,
    total,
    insights,
    isLoading,
    error,
    createSession,
    fetchSessions,
    rateSession,
    deleteSession,
    fetchInsights
  };
}
